import { useEffect, useMemo, useRef, useState } from 'react';
import { copyText } from '../lib/clipboard';
import { downloadText } from '../lib/download';
import { EXPORT_FORMATS, type ExportFormatId } from '../lib/exporters';
import { splitTiers } from '../lib/extract';
import { harmonies, isAchromatic } from '../lib/harmony';
import styles from './ExportTabs.module.css';

interface ExportTabsProps {
  /** The dominant palette (extracted swatches). */
  colors: string[];
  /** Currently selected base color, or null before any image is loaded. */
  baseColor: string | null;
}

interface ExportSource {
  id: string;
  label: string;
  colors: string[];
}

/**
 * The set of color groups a user can export: each palette tier, the whole
 * palette when there is more than one tier, plus the three harmony sets derived
 * from the base color. Harmonies are omitted for an achromatic base (they
 * collapse to duplicates of the base — see `harmony.ts`).
 */
function buildSources(colors: string[], baseColor: string | null): ExportSource[] {
  const tiers = splitTiers(colors);
  const sources: ExportSource[] = tiers.map((t) => ({ id: t.id, label: t.label, colors: t.colors }));
  if (tiers.length > 1) sources.push({ id: 'all', label: 'All colors', colors });
  if (baseColor && !isAchromatic(baseColor)) {
    for (const h of harmonies(baseColor)) {
      sources.push({ id: h.kind, label: h.label, colors: h.colors });
    }
  }
  return sources;
}

/**
 * Export panel: pick a color source (palette or a harmony set), pick a format
 * (CSS / Tailwind / Figma tokens / raw JSON), and copy the result. Output is
 * derived purely from the displayed colors via the shared exporter registry.
 */
export function ExportTabs({ colors, baseColor }: ExportTabsProps) {
  const sources = useMemo(() => buildSources(colors, baseColor), [colors, baseColor]);
  const [sourceId, setSourceId] = useState<string>('primary');
  const [formatId, setFormatId] = useState<ExportFormatId>('css');
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The selected source may disappear (e.g. base color changed to gray, or a
  // new image has no secondary tier); fall back to the first so we never
  // render against a stale id.
  const source = sources.find((s) => s.id === sourceId) ?? sources[0];
  const format = EXPORT_FORMATS.find((f) => f.id === formatId) ?? EXPORT_FORMATS[0];
  const output = format.serialize(source.colors);

  // Reset the "Copied!" state whenever what we'd copy changes.
  useEffect(() => {
    setCopied(false);
  }, [output]);

  useEffect(() => () => {
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
  }, []);

  if (colors.length === 0) return null;

  const handleCopy = async () => {
    const ok = await copyText(output);
    setCopied(ok);
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    if (ok) copiedTimer.current = setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => downloadText(output, format.filename, format.mimeType);

  return (
    <section aria-label="Export palette" className={styles.exports}>
      {sources.length > 1 && (
        <div className={styles.field}>
          <p className={styles.groupLabel} id="export-source-label">
            Source
          </p>
          <div className={styles.sources} role="group" aria-labelledby="export-source-label">
            {sources.map((s) => (
              <button
                key={s.id}
                type="button"
                className={styles.source}
                aria-pressed={s.id === source.id}
                onClick={() => setSourceId(s.id)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={styles.field}>
        <p className={styles.groupLabel} id="export-format-label">
          Format
        </p>
        <div className={styles.tabs} role="tablist" aria-labelledby="export-format-label">
          {EXPORT_FORMATS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              id={`export-tab-${f.id}`}
              aria-selected={f.id === format.id}
              aria-controls="export-panel"
              className={styles.tab}
              onClick={() => setFormatId(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div
        id="export-panel"
        role="tabpanel"
        aria-labelledby={`export-tab-${format.id}`}
        className={styles.panel}
      >
        <div className={styles.actions}>
          <button type="button" className={styles.action} onClick={handleCopy}>
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <button
            type="button"
            className={styles.action}
            onClick={handleDownload}
            aria-label={`Download as ${format.filename}`}
          >
            Download
          </button>
        </div>
        <pre className={styles.code}>
          <code>{output}</code>
        </pre>
      </div>
    </section>
  );
}
